import { ApplicationRef, createNgModuleRef, Inject, Injectable, Injector, NgModuleRef } from '@angular/core';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Acl } from '@df/core/service/acl';
import { Client } from '@df/core/service/client';
import { CookiesService } from '@df/core/service/cookies';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ICmsAdminEditorModule } from '../../admin/editor/icms-admin-editor.module';
import { ICmsClientOpenModule } from '../../client/open/icms-client-open.module';
import { ICmsPluginLoaderService } from '../../plugin/icms-plugin-loader.service';
import { EICmsClientPermissions, ICMS_CLIENT_CONFIG, NICmsClientConfig } from '../config/icms-client.config';
import { ICmsClientAdminLoaderModule } from './icms-client-admin-loader.module';
import { EdependencyLevel } from '@df/catalog/model';

export namespace NICmsClientAdminLoader {
    export const stateStorageKey = 'icmsAdminState';
    export const loaderSessionKey = 'icmsLoader';
}

@Injectable({ providedIn: 'root' })
export class ICmsClientAdminLoaderService {
    /**
     * Editor module ref
     */
    private editorModuleRef?: NgModuleRef<ICmsAdminEditorModule>;
    /**
     * Toggle module ref
     */
    private toggleModuleRef?: NgModuleRef<ICmsClientOpenModule>;
    /**
     * Loader module ref
     */
    private loaderModuleRef?: NgModuleRef<ICmsClientAdminLoaderModule>;

    /**
     * Root DOM element of the standard application
     */
    private rootElement?: HTMLElement;

    /**
     * Parent element of the DOM root element
     */
    private rootParent!: HTMLElement | null;

    /**
     * If editor is open
     */
    state: BehaviorSubject<boolean>;

    /**
     * If service is initiated (first)
     */
    private initiated = false;

    private readonly editorExpectedState$ = new BehaviorSubject(false);

    /**
     * Dependencies
     */
    constructor(
        private injector: Injector,
        private applicationRef: ApplicationRef,
        private acl: Acl,
        private meta: Meta,
        client: Client,
        private iCmsPluginLoaderService: ICmsPluginLoaderService,
        private blockStickyService: BlockStickyService,
        private app: App,
        @Inject(ICMS_CLIENT_CONFIG) private clientConfig: NICmsClientConfig.IConfig,
        cookiesService: CookiesService
    ) {
        this.state = new BehaviorSubject(this.getStorageState());
        this.iCmsPluginLoaderService.init(this.editorExpectedState$);
        const cmsCookie = '_isCms';
        this.state.subscribe(state => {
            client.isCms = state;
            state ? this.blockStickyService.disallow(this) : this.blockStickyService.allow(this);
            if (this.initiated) {
                this.setStorageState(state);
                this.updateToggle();
            }
            state ? cookiesService.set(cmsCookie, '1') : cookiesService.delete(cmsCookie);
        });
        acl.authStatus.subscribe(() => {
            if (this.initiated) {
                this.updateEditor();
                this.updateToggle();
            }
        });
        this.updateEditor();
        this.updateToggle();
        this.initiated = true;
        this.iCmsPluginLoaderService.loadClientModules(this.injector);
    }

    /**
     * Open CMS area
     */
    async openEditor() {
        if (!this.acl.check(EICmsClientPermissions.cms)) {
            return;
        }
        if (!this.editorExpectedState$.value) {
            this.editorExpectedState$.next(true);
        }
        const promise = this.loadEditorModule();
        if (!this.sessionLoader || !this.getStorageState()) {
            await this.createLoader();
        }
        if (!this.editorModuleRef) {
            await promise;
        }

        this.setUpMockupResources();

        if (this.checkEditorWithExpectedState()) {
            this.loadEditor();
        }
    }

    async loadEditor(): Promise<void> {
        const model = this.meta.model;

        if (model && model.verbosity! < model.maxVerbosity) {
            await model.load(model.maxVerbosity, { dependencyLevel: EdependencyLevel.details, safe: true });
        }

        if (this.editorModuleRef) {
            await this.iCmsPluginLoaderService.loadAdminModules(this.editorModuleRef.instance.injector);
        }

        if (!this.checkEditorWithExpectedState()) {
            return;
        }

        this.editorModuleRef!.instance.createEditor();

        if (!this.state.value) {
            this.state.next(true);
        }

        this.checkEditorWithExpectedState();
    }

    private setUpMockupResources() {
        if (this.clientConfig.resourcesMockup) {
            Object.keys(this.clientConfig.resourcesMockup).forEach(key => {
                const resource = this.app.resources[key];
                if (resource) {
                    this.clientConfig.resourcesMockup?.[key].forEach(data => {
                        resource.toModel(data.id).setData(data);
                    });
                }
            });
        }
    }

    private cleanUpMockupResources() {
        if (this.clientConfig.resourcesMockup) {
            Object.keys(this.clientConfig.resourcesMockup).forEach(key => {
                const resource = this.app.resources[key];
                if (resource) {
                    this.clientConfig.resourcesMockup?.[key].forEach(data => {
                        resource.unload(data.id);
                    });
                }
            });
        }
    }

    private checkEditorWithExpectedState() {
        if (!this.editorExpectedState$.value) {
            this.closeEditor();
            this.destroyLoader();
            return false;
        }
        return true;
    }

    /**
     * Restore application into its original position
     */
    closeEditor() {
        if (this.editorExpectedState$.value) {
            this.editorExpectedState$.next(false);
        }
        // this.destroyLoader()
        if (this.editorModuleRef) {
            if (this.rootParent) {
                this.rootParent.prepend(this.rootElement!);
            }
            this.cleanUpMockupResources();
            this.editorModuleRef.instance.destroyEditor();
            this.editorModuleRef.destroy();
            this.editorModuleRef = undefined;
        }
        this.iCmsPluginLoaderService.destroyAdminModules();
        if (this.state.value) {
            this.state.next(false);
        }
    }

    /**
     * Move application into cms wrapper
     */
    async embedAppIn(wrapper: HTMLElement) {
        await this.initiateRoot();

        wrapper.prepend(this.rootElement!);
    }

    /**
     * Set toggle state depending on app status
     */
    private updateToggle() {
        if (this.state.value || !this.acl.check(EICmsClientPermissions.cms)) {
            this.destroyToggle();
        } else {
            this.createToggle();
        }
    }

    /**
     * Set toggle state depending on app status
     */
    private updateEditor() {
        if (!this.state.value || !this.acl.check(EICmsClientPermissions.cms)) {
            this.closeEditor();
        } else {
            this.openEditor();
        }
    }

    private async createToggle() {
        const module = await this.loadToggle();
        await this.loadLoaderModule();
        module.instance.createToggle();
    }

    private async createLoader() {
        const module = await this.loadLoaderModule();
        await module.instance.createLoader();
        this.subscribeLoaderState();
    }

    /**
     * Remove state toggler
     */
    private destroyToggle() {
        this.toggleModuleRef?.instance.destroyToggle();
    }

    private destroyLoader() {
        this.loaderModuleRef?.instance.destroyLoader();
    }

    /**
     * Loads editor module
     */
    private async loadEditorModule() {
        await this.loadLoaderModule();
        const module = await import(/* webpackChunkName: "icms-editor.aw" */ '../../admin/editor/icms-admin-editor.module');
        if (!this.editorModuleRef) {
            this.editorModuleRef = createNgModuleRef(module.ICmsAdminEditorModule, this.injector);
        }
        await this.editorModuleRef.instance.load();
        return this.editorModuleRef;
    }

    /**
     * Loads editor module
     */
    private async loadToggle(): Promise<NgModuleRef<ICmsClientOpenModule>> {
        if (this.toggleModuleRef) {
            return this.toggleModuleRef;
        }

        const module = await import(/* webpackChunkName: "icms-open.aw" */ '../../client/open/icms-client-open.module');
        if (!this.toggleModuleRef) {
            this.toggleModuleRef = createNgModuleRef(module.ICmsClientOpenModule, this.injector);
        }
        return this.toggleModuleRef;
    }

    /**
     * Loads loader module
     */
    private async loadLoaderModule(): Promise<NgModuleRef<ICmsClientAdminLoaderModule>> {
        if (this.loaderModuleRef) {
            return this.loaderModuleRef;
        }

        const module = await import(/* webpackChunkName: "icms-loader.aw" */ './icms-client-admin-loader.module');
        if (!this.loaderModuleRef) {
            this.loaderModuleRef = createNgModuleRef(module.ICmsClientAdminLoaderModule, this.injector);
        }
        return this.loaderModuleRef;
    }

    private initiateRoot(): Promise<void> {
        return new Promise(resolve => {
            if (this.rootElement) {
                resolve();
            } else {
                let interval = setInterval(() => {
                    this.rootElement = this.applicationRef.components[0]?.location.nativeElement;

                    if (!this.rootElement) {
                        return;
                    }

                    if (this.rootElement) {
                        this.rootParent = this.rootElement.parentElement;
                    }

                    clearInterval(interval);

                    resolve();
                }, 100);
            }
        });
    }

    /**
     * Gets current state basing on storage value
     */
    private getStorageState(): boolean {
        return typeof window !== 'undefined' && !!window.localStorage.getItem(NICmsClientAdminLoader.stateStorageKey);
    }

    /**
     * Saves current state in storage
     */
    private setStorageState(state = this.state.value): void {
        if (typeof window !== 'undefined') {
            state
                ? window.localStorage.setItem(NICmsClientAdminLoader.stateStorageKey, '1')
                : window.localStorage.removeItem(NICmsClientAdminLoader.stateStorageKey);
        }
    }

    /**
     * Saves if loader already animated in this session.
     */
    private setSessionLoader() {
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(NICmsClientAdminLoader.loaderSessionKey, '1');
        }
    }

    private get sessionLoader(): boolean {
        return typeof window !== 'undefined' && !!window.sessionStorage.getItem(NICmsClientAdminLoader.loaderSessionKey);
    }

    subscribeLoaderState(): void {
        if (this.loaderModuleRef) {
            let subscription: Subscription;

            const destroy = () => {
                subscription?.unsubscribe();
                this.setSessionLoader();
                this.destroyLoader();
            };

            this.loaderModuleRef!.instance.animationState!.subscribe(async animationState => {
                if (animationState.logoInit === 'finished' && animationState.fadeOut === undefined) {
                    subscription = this.state.subscribe(state => {
                        if (state) {
                            this.loaderModuleRef?.instance.fadeOut();
                            subscription?.unsubscribe();
                        } else {
                            destroy();
                        }
                    });
                }
                if (animationState.logoInit === 'finished' && animationState.fadeOut === 'finished') {
                    destroy();
                }
            });
        }
    }
}
